/**
 * Slice 56 / stage 3 — what the cleaner's screens say about signal. One calm line, never a button.
 *   no signal      → "No signal. Photos and answers are saved on this phone and send later."
 *   weak signal    → same, "Weak signal … send when they can."
 *   back in signal → "Back in signal. Sending what was saved."  then a short "Sent".
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { getOutboxCounts, hasStalledItems, useOutboxVersion } from "@/lib/offline/outbox";
import { getOutboxEntries, useStampOutboxVersion } from "@/lib/stampOutbox";

export type SignalLine = "none" | "offline" | "weak" | "sending" | "sent" | "reopened";

function useIsOnline(): boolean {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

export function useOfflineStatus() {
  const online = useIsOnline();
  const v1 = useOutboxVersion();
  const v2 = useStampOutboxVersion();
  const counts = useMemo(() => getOutboxCounts(), [v1]);
  const stampWaiting = useMemo(() => getOutboxEntries().length, [v2]);
  const waiting = counts.writes + counts.photos + stampWaiting;
  const stalled = useMemo(() => hasStalledItems(), [v1]);

  // Progress of the current sending round: the most that was waiting, and how many have gone since.
  const batchTotal = useRef(0);
  if (waiting === 0) batchTotal.current = 0;
  else if (waiting > batchTotal.current) batchTotal.current = waiting;
  const progress = { done: Math.max(0, batchTotal.current - waiting), total: batchTotal.current };

  const [flash, setFlash] = useState<"sent" | "reopened" | null>(null);
  const hadWaiting = useRef(false);
  const startedOffline = useRef(typeof navigator !== "undefined" && navigator.onLine === false);

  useEffect(() => {
    if (waiting > 0) {
      hadWaiting.current = true;
      return;
    }
    if (hadWaiting.current) {
      hadWaiting.current = false;
      setFlash("sent");
      const t = window.setTimeout(() => setFlash(null), 4000);
      return () => window.clearTimeout(t);
    }
  }, [waiting]);

  // Opened again with no signal and something still waiting: one short line that fades.
  useEffect(() => {
    if (startedOffline.current && waiting > 0) {
      startedOffline.current = false;
      setFlash("reopened");
      const t = window.setTimeout(() => setFlash((f) => (f === "reopened" ? null : f)), 3500);
      return () => window.clearTimeout(t);
    }
  }, [waiting]);

  let line: SignalLine = "none";
  if (!online) line = "offline";
  else if (waiting > 0) line = stalled ? "weak" : "sending";
  else if (flash) line = flash;
  if (!online && flash === "reopened") line = "reopened";

  return { online, line, waiting, photos: counts.photos, failed: counts.failed, progress };
}
